import { z } from 'zod';
import { parseMoneyInput, parsePercentInput } from './money';

/**
 * Regras de campo comuns a vários formulários. Espelham o que o backend valida para avisar antes
 * de enviar — quem decide continua sendo o servidor, e a mensagem dele vence.
 */
export const requiredEmail = z
  .string()
  .trim()
  .min(1, 'Informe o e-mail.')
  .email('E-mail inválido.');

/** Texto livre opcional: vazio vira `null`, que é como a API distingue "não informado". */
export const optionalText = z
  .string()
  .trim()
  .transform((value) => (value === '' ? null : value));

/**
 * Valor em dinheiro digitado ("20", "20,50", "1.234,56") para o formato da API ("20.50"). Continua
 * texto: dinheiro nunca vira `number`. Opcional vazio é `null` — desconhecido não é zero.
 */
export function moneyInput({ required }: { required: boolean }) {
  return z.string().transform((value, context) => {
    if (value.trim() === '') {
      if (required) context.addIssue({ code: 'custom', message: 'Informe o valor.' });
      return null;
    }
    const parsed = parseMoneyInput(value);
    if (parsed === null) {
      context.addIssue({ code: 'custom', message: 'Valor inválido. Ex.: 20 ou 20,50.' });
      return z.NEVER;
    }
    return parsed;
  });
}

/** Percentual digitado ("16", "6,5%") para fração ("0.16"), como a API guarda. */
export function percentInput({ required }: { required: boolean }) {
  return z.string().transform((value, context) => {
    if (value.trim() === '') {
      if (required) context.addIssue({ code: 'custom', message: 'Informe o percentual.' });
      return null;
    }
    const parsed = parsePercentInput(value);
    if (parsed === null) {
      context.addIssue({ code: 'custom', message: 'Percentual inválido. Ex.: 16 ou 6,5.' });
      return z.NEVER;
    }
    return parsed;
  });
}

/** Inteiro digitado entre `min` e `max`, com a mensagem dizendo a faixa. */
export function intInput(min: number, max: number) {
  const message = `Número inteiro de ${min} a ${max}.`;
  return z
    .string()
    .trim()
    .regex(/^\d+$/, message)
    .transform(Number)
    .pipe(z.number().min(min, message).max(max, message));
}

/** "25,5" → "25.5" para medida positiva; `null` se inválida. */
function parseMeasure(input: string): string | null {
  const value = input.trim().replace(',', '.');
  return /^\d{1,6}(\.\d{1,3})?$/.test(value) && Number(value) > 0 ? value : null;
}

/** Medida digitada ("25,5") para a API ("25.5"). Opcional vazio é `null`. */
export function measureInput({ required, message }: { required: boolean; message: string }) {
  return z.string().transform((value, context) => {
    if (value.trim() === '') {
      if (required) context.addIssue({ code: 'custom', message });
      return null;
    }
    const parsed = parseMeasure(value);
    if (parsed === null) {
      context.addIssue({ code: 'custom', message: 'Medida inválida. Ex.: 25 ou 25,5.' });
      return z.NEVER;
    }
    return parsed;
  });
}

/** Decimal da API ("25.5") para o campo digitado ("25,5"); `null` vira campo vazio. */
export function decimalToInput(value: string | null): string {
  return value === null ? '' : value.replace('.', ',');
}
